import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseHttpService } from '../../../shared/services/base-http.service';
import { AuthService } from '../../../shared/services/auth.service';
import { Prompt, GlobalResponse } from '../interfaces/prompt.interface';

@Injectable({
  providedIn: 'root'
})
export class PromptsService {
  private readonly base = '/Prompts';

  constructor(private http: BaseHttpService, private authService: AuthService) {}

  private normalizePrompt(raw: any): Prompt {
    const normalizedType = String(raw?.type ?? raw?.Type ?? '')
      .trim()
      .toUpperCase();

    return {
      id: String(raw?.id ?? raw?.Id ?? ''),
      text: String(raw?.text ?? raw?.Text ?? ''),
      type: normalizedType,
      category: raw?.category ?? raw?.Category ?? undefined,
      isSystem: raw?.isSystem ?? raw?.IsSystem ?? undefined,
      createdAt: raw?.createdAt ?? raw?.CreatedAt ?? undefined,
      updatedAt: raw?.updatedAt ?? raw?.UpdatedAt ?? undefined
    };
  }

  getPrompts(promptType: string, category?: string, page: number = 1, pageSize: number = 12): Observable<Prompt[] | null> {
    const params: Record<string, string | number | boolean> = {
      type: promptType,
      page,
      pageSize
    };
    if (category && category !== 'All Categories') {
      params['category'] = category;
    }

    return this.http.get<GlobalResponse<Prompt[]>>(`${this.base}/GetPrompts`, params).pipe(
      map(res => res && res.data ? (res.data as any[]).map((p) => this.normalizePrompt(p)) : null),
      catchError(err => {
        console.error('Error loading prompts', err);
        return of(null);
      })
    );
  }

  private getNewPromptType(): string {
    return this.authService.hasRole('Admin') ? 'S' : 'U';
  }

  createPrompt(text: string, category?: string): Observable<Prompt | null> {
    const payload: any = {
      Text: text,
      Type: this.getNewPromptType()
    };
    if (category && category.trim().length > 0) {
      payload.Category = category.trim();
    }

    return this.http.post<GlobalResponse<Prompt>>(`${this.base}/CreatePrompt`, payload).pipe(
      map(res => res && res.data ? this.normalizePrompt(res.data) : null),
      catchError(err => {
        console.error('Error creating prompt', err);
        return of(null);
      })
    );
  }

  updatePrompt(id: string, payload: Partial<Prompt>): Observable<Prompt | null> {
    return this.http.put<GlobalResponse<Prompt>>(`${this.base}/UpdatePrompt/${id}`, payload).pipe(
      map(res => res && res.data ? this.normalizePrompt(res.data) : null),
      catchError(err => {
        console.error('Error updating prompt', err);
        return of(null);
      })
    );
  }

  deletePrompt(id: string): Observable<boolean> {
    return this.http.delete<GlobalResponse<boolean>>(`${this.base}/DeletePrompt/${id}`).pipe(
      map(res => !!(res && res.data === true)),
      catchError(err => {
        console.error('Error deleting prompt', err);
        return of(false);
      })
    );
  }
}
